import React, { useEffect, useState, useCallback } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { taskAssignmentsApi, TASK_STATUSES, statusLabel } from "./taskAssignmentsApi";
import TaskProgressPanel from "./TaskProgressPanel";
import { showErrorToast } from "../../constants/ToastService";
import "./TaskAssignments.css";

/**
 * Assignee view: only the tasks assigned to the logged-in user. Dates,
 * description and assignee are read-only here; the user can advance status +
 * completion percentage (with a mandatory comment) via the progress panel.
 */
const MyTasks = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const loadTasks = useCallback(async () => {
        setLoading(true);
        try {
            const params = statusFilter ? { status: statusFilter } : {};
            const res = await taskAssignmentsApi.getMyTasks(params);
            setTasks(res.data || []);
        } catch (err) {
            showErrorToast(err.message || "Failed to load your tasks");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

    return (
        <div className="ta-page">
            <div className="ta-header">
                <div>
                    <h1 className="ta-title">My Tasks</h1>
                    <p className="ta-subtitle">Tasks assigned to you. Update status and completion — a comment is required each time.</p>
                </div>
            </div>

            <div className="ta-filters">
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All Statuses</option>
                    {TASK_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <p className="ta-muted">Loading your tasks…</p>
            ) : tasks.length === 0 ? (
                <p className="ta-muted">You have no tasks assigned.</p>
            ) : (
                <div className="ta-cards">
                    {tasks.map((task) => (
                        <div key={task.id} className="ta-card">
                            <div className="ta-card__head" onClick={() => toggleExpand(task.id)}>
                                <div className="ta-card__main">
                                    <span className="ta-code">{task.task_code}</span>
                                    <span className="ta-card__title">{task.title}</span>
                                </div>
                                <div className="ta-card__meta">
                                    <span className={`ta-status ta-status--${task.status}`}>{statusLabel(task.status)}</span>
                                    <span className="ta-dates">{task.start_date} → {task.end_date}</span>
                                    <span className="ta-progress-cell">
                                        <span className="ta-bar"><span className="ta-bar__fill" style={{ width: `${task.completion_percentage || 0}%` }} /></span>
                                        <span className="ta-pct">{task.completion_percentage || 0}%</span>
                                    </span>
                                    {expandedId === task.id ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                                </div>
                            </div>
                            {expandedId === task.id && (
                                <div className="ta-accordion">
                                    {task.description && <p className="ta-desc">{task.description}</p>}
                                    <TaskProgressPanel task={task} canEdit={true} onUpdated={loadTasks} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyTasks;
